import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageContent } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, RefreshCw, Trash2, ExternalLink, Check, X, FileInput, Edit2, Wifi, Send, Facebook, Instagram, Twitter } from "lucide-react";
import { toast } from "sonner";

const SOCIAL_PLATFORMS = [
  { id: "telegram", label: "Telegram", icon: Send, color: "text-sky-300" },
  { id: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-400" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "text-pink-400" },
  { id: "x", label: "X", icon: Twitter, color: "text-slate-200" },
];

export default function RSSFeeds() {
  const [feeds, setFeeds] = useState([]);
  const [articles, setArticles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", active: true, priority: 50, tags: "", channel_ids: [], social_platforms: ["telegram"] });
  const [tab, setTab] = useState("pending");
  const [feedSearch, setFeedSearch] = useState("");

  const load = async () => {
    const [f, a, c] = await Promise.all([
      api.get("/rss/feeds"),
      api.get("/rss/articles"),
      api.get("/telegram/channels"),
    ]);
    setFeeds(f.data || []);
    setArticles(a.data || []);
    setChannels(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", url: "", active: true, priority: 50, tags: "", channel_ids: [], social_platforms: ["telegram"] });
    setOpen(true);
  };

  const openEdit = (feed) => {
    setEditing(feed);
    setForm({
      name: feed.name || "",
      url: feed.url || "",
      active: feed.active !== false,
      priority: feed.priority ?? 50,
      tags: (feed.tags || []).join(", "),
      channel_ids: feed.channel_ids || [],
      social_platforms: feed.social_platforms || ["telegram"],
    });
    setOpen(true);
  };

  const toggleCh = (id) => {
    setForm(f => ({
      ...f,
      channel_ids: f.channel_ids.includes(id)
        ? f.channel_ids.filter(x => x !== id)
        : [...f.channel_ids, id],
    }));
  };

  const togglePlatform = (id, enabled) => {
    setForm(f => {
      const current = new Set(f.social_platforms || []);
      if (enabled) current.add(id);
      else current.delete(id);
      return { ...f, social_platforms: Array.from(current) };
    });
  };

  const save = async () => {
    if (!form.name || !form.url) { toast.error("Compila i campi"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        url: form.url,
        active: form.active !== false,
        priority: parseInt(form.priority) || 50,
        tags: (form.tags || "").split(",").map(s => s.trim()).filter(Boolean),
        channel_ids: form.channel_ids,
        social_platforms: form.social_platforms || [],
      };
      if (editing) {
        const { data } = await api.patch(`/rss/feeds/${editing.id}`, payload);
        setFeeds(current => current.map(feed => feed.id === editing.id ? data : feed));
        toast.success("Feed aggiornato");
      } else {
        const { data } = await api.post("/rss/feeds", payload);
        setFeeds(current => [data, ...current]);
        toast.success("Feed aggiunto");
      }
      setOpen(false);
      setEditing(null);
      setForm({ name: "", url: "", active: true, priority: 50, tags: "", channel_ids: [], social_platforms: ["telegram"] });
      load().catch(() => toast.error("Feed salvato, ma l'aggiornamento della lista non è riuscito"));
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  const refresh = async () => { try { await api.post("/rss/fetch"); toast.success("Aggiornamento avviato"); setTimeout(load, 1500); } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento feed"); } };
  const removeF = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/rss/feeds/${id}`); load(); };
  const testFeed = async (id) => { try { const { data } = await api.post(`/rss/feeds/${id}/test`); toast.success(`Feed valido: ${data.entries} articoli`); } catch (e) { toast.error(e?.response?.data?.detail || "Feed non valido"); } };
  const toggleFeedActive = async (feed) => { await api.patch(`/rss/feeds/${feed.id}`, { active: feed.active === false }); load(); };
  const setStatus = async (id, status) => { await api.patch(`/rss/articles/${id}`, { status }); load(); };
  const importA = async (id) => { try { await api.post(`/rss/articles/${id}/import`); toast.success("Importato come bozza"); load(); } catch { toast.error("Errore"); } };

  const filteredFeeds = feeds.filter(f => !feedSearch || `${f.name} ${f.url} ${(f.tags||[]).join(" ")}`.toLowerCase().includes(feedSearch.toLowerCase()));
  const filtered = articles.filter(a => tab === "all" || a.status === tab);

  return (
    <div data-testid="rss-page">
      <PageHeader title="Feed RSS" subtitle="Importa automaticamente articoli e approva la pubblicazione.">
        <Button variant="outline" onClick={refresh} data-testid="rss-refresh" className="border-slate-700"><RefreshCw size={16} className="mr-1" />Aggiorna feed</Button>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild><Button data-testid="add-feed-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-1" />Aggiungi feed</Button></DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 w-[calc(100vw-2rem)] max-w-2xl max-h-[calc(100vh-2rem)] overflow-hidden p-0 grid grid-rows-[auto_minmax(0,1fr)_auto]">
            <DialogHeader className="px-4 pr-12 pt-5 sm:px-6 sm:pr-12 sm:pt-6"><DialogTitle>{editing ? "Modifica feed RSS" : "Nuovo feed RSS"}</DialogTitle></DialogHeader>
            <div className="min-w-0 space-y-3 overflow-y-auto overflow-x-hidden px-4 py-1 sm:px-6">
              <div><Label>Nome</Label><Input data-testid="feed-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="Gazzetta dello Sport" /></div>
              <div><Label>URL feed</Label><Input data-testid="feed-url" value={form.url} onChange={e=>setForm({...form, url:e.target.value})} className="bg-slate-950 border-slate-800 mt-1 font-mono text-xs" /></div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label>Priorità</Label><Input type="number" value={form.priority} onChange={e=>setForm({...form, priority:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
                <div className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-md px-3 mt-6 h-10"><Label>Attivo</Label><Switch checked={!!form.active} onCheckedChange={v=>setForm({...form, active:v})} /></div>
              </div>
              <div><Label>Tag / categorie feed</Label><Input value={form.tags} onChange={e=>setForm({...form, tags:e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="calcio, juventus" /></div>
              <div>
                <Label>Piattaforme automatiche</Label>
                <p className="text-xs text-slate-500 mb-2 mt-0.5">Ogni nuova notizia del feed verrà pubblicata sulle piattaforme attive. Facebook, Instagram e X usano solo API ufficiali.</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {SOCIAL_PLATFORMS.map(platform => {
                    const Icon = platform.icon;
                    const checked = (form.social_platforms || []).includes(platform.id);
                    return (
                      <div key={platform.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-md px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <Icon size={15} className={platform.color} />
                          <span className="text-sm text-white">{platform.label}</span>
                        </div>
                        <Switch checked={checked} onCheckedChange={v=>togglePlatform(platform.id, v)} />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Canali Telegram associati</Label>
                <p className="text-xs text-slate-500 mb-2 mt-0.5">Gli articoli di questo feed saranno auto-pubblicati sui canali selezionati (se l'auto-pubblicazione è attiva).</p>
                {channels.length === 0 ? <div className="text-xs text-slate-500 p-3 border border-dashed border-slate-800 rounded-md">Nessun canale configurato.</div> :
                  <div className="grid max-h-48 grid-cols-1 gap-2 overflow-y-auto overflow-x-hidden scrollbar-thin sm:grid-cols-2">
                    {channels.map(c => (
                      <button key={c.id} type="button" onClick={()=>toggleCh(c.id)} data-testid={`feed-ch-${c.id}`}
                        className={`min-w-0 w-full text-left px-3 py-2 rounded-md border text-sm ${form.channel_ids.includes(c.id) ? "bg-blue-600/20 border-blue-500/50 text-blue-200" : "bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700"}`}>
                        <div className="font-medium break-words">{c.name}</div>
                        <div className="text-[10px] font-mono break-all">{c.chat_id}</div>
                      </button>
                    ))}
                  </div>}
              </div>
            </div>
            <DialogFooter className="gap-2 border-t border-slate-800 bg-slate-900 px-4 py-4 sm:px-6">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annulla</Button>
              <Button type="button" onClick={save} disabled={saving} data-testid="feed-save" className="bg-blue-600 hover:bg-blue-700">
                {saving ? "Salvataggio..." : editing ? "Salva modifiche" : "Salva"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      <PageContent>
        <div>
          <h3 className="text-sm uppercase tracking-wider font-mono text-slate-400 mb-3">Sorgenti ({feeds.length})</h3>
          <Input value={feedSearch} onChange={e=>setFeedSearch(e.target.value)} className="bg-slate-900 border-slate-800 mb-3 max-w-lg" placeholder="Cerca feed per nome, URL o tag" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {filteredFeeds.length === 0 && <div className="col-span-full text-sm text-slate-500 py-8 text-center border border-dashed border-slate-800 rounded-md">Nessun feed configurato.</div>}
            {filteredFeeds.map(f => (
              <div key={f.id} className="bg-slate-900 border border-slate-800 rounded-md p-4" data-testid={`feed-${f.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-bold text-white truncate">{f.name}</div>
                    <a href={f.url} target="_blank" rel="noreferrer" className="text-xs text-blue-400 truncate font-mono flex items-center gap-1"><ExternalLink size={10} />{f.url}</a>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Switch checked={f.active !== false} onCheckedChange={()=>toggleFeedActive(f)} />
                    <Button size="sm" variant="ghost" onClick={()=>testFeed(f.id)} className="h-7 text-emerald-400"><Wifi size={13} /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>openEdit(f)} className="h-7"><Edit2 size={13} /></Button>
                    <Button size="sm" variant="ghost" onClick={()=>removeF(f.id)} className="h-7 text-red-400"><Trash2 size={13} /></Button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(f.channel_ids||[]).length === 0 ? <span className="text-[10px] text-slate-600 italic">Nessun canale associato</span> :
                    (f.channel_ids||[]).map(cid => {
                      const ch = channels.find(c => c.id === cid);
                      return <span key={cid} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600/20 text-blue-300 border border-blue-500/30">{ch?.name || cid.slice(0,8)}</span>;
                    })}
                </div>
                <div className="text-[10px] text-slate-500 mt-2 font-mono">{f.last_fetched ? `↻ ${new Date(f.last_fetched).toLocaleString("it-IT")}` : "Mai aggiornato"}</div>
                <div className="text-[10px] text-slate-500 mt-1 font-mono">Priorità {f.priority ?? 50} · {f.active === false ? "disattivo" : "attivo"}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(f.social_platforms || ["telegram"]).map(platform => (
                    <span key={platform} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700 uppercase">{platform}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="pending" data-testid="tab-pending">In attesa</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approvati</TabsTrigger>
            <TabsTrigger value="imported" data-testid="tab-imported">Importati</TabsTrigger>
            <TabsTrigger value="auto_published" data-testid="tab-auto">Auto-pubblicati</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rifiutati</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Tutti</TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-4 space-y-2">
            {filtered.length === 0 && <div className="text-sm text-slate-500 py-8 text-center">Nessun articolo.</div>}
            {filtered.map(a => (
              <div key={a.id} className="bg-slate-900 border border-slate-800 rounded-md p-4 hover:border-slate-700" data-testid={`article-${a.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 font-mono uppercase">{a.feed_name}</span>
                      <span className="text-[10px] text-slate-500 font-mono">{a.status}</span>
                    </div>
                    <a href={a.link} target="_blank" rel="noreferrer" className="text-white font-semibold hover:text-blue-400 block">{a.title}</a>
                    <div className="text-sm text-slate-400 mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: a.summary?.slice(0, 250) }} />
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" onClick={()=>importA(a.id)} data-testid={`import-${a.id}`} className="bg-blue-600 hover:bg-blue-700 h-8"><FileInput size={13} className="mr-1" />Importa</Button>
                    <Button size="sm" variant="ghost" onClick={()=>setStatus(a.id, "approved")} className="h-7 text-emerald-400"><Check size={13} className="mr-1" />Ok</Button>
                    <Button size="sm" variant="ghost" onClick={()=>setStatus(a.id, "rejected")} className="h-7 text-red-400"><X size={13} className="mr-1" />Scarta</Button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </PageContent>
    </div>
  );
}
